import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
    Bell, Heart, MessageCircle, Star, DollarSign,
    Package, Shield, CheckCheck, ArrowLeft, RefreshCw,
    ChevronLeft, ChevronRight, Eye, Truck, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_CONFIG = {
    all: { label: 'All', icon: Bell, color: 'from-purple-500 to-indigo-500', bg: 'bg-purple-500/10', text: 'text-purple-400' },
    social: { label: 'Social', icon: Heart, color: 'from-pink-500 to-rose-500', bg: 'bg-pink-500/10', text: 'text-pink-400' },
    comics: { label: 'Comics', icon: Star, color: 'from-amber-500 to-orange-500', bg: 'bg-amber-500/10', text: 'text-amber-400' },
    earnings: { label: 'Earnings', icon: DollarSign, color: 'from-green-500 to-emerald-500', bg: 'bg-green-500/10', text: 'text-green-400' },
    orders: { label: 'Orders', icon: Package, color: 'from-blue-500 to-sky-500', bg: 'bg-blue-500/10', text: 'text-blue-400' },
    system: { label: 'System', icon: Shield, color: 'from-gray-500 to-slate-500', bg: 'bg-gray-500/10', text: 'text-gray-400' },
};

const TYPE_ICONS = {
    like: { icon: Heart, color: 'text-pink-400', bg: 'bg-pink-500/20' },
    comment: { icon: MessageCircle, color: 'text-cyan-400', bg: 'bg-cyan-500/20' },
    featured: { icon: Star, color: 'text-amber-400', bg: 'bg-amber-500/20' },
    tip: { icon: DollarSign, color: 'text-green-400', bg: 'bg-green-500/20' },
    order: { icon: Package, color: 'text-blue-400', bg: 'bg-blue-500/20' },
    system: { icon: Shield, color: 'text-gray-400', bg: 'bg-gray-500/20' },
    follow: { icon: Heart, color: 'text-purple-400', bg: 'bg-purple-500/20' },
};

const Notifications = () => {
    const { API, user } = useContext(AuthContext);
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('all');
    const [total, setTotal] = useState(0);
    const [unreadCount, setUnreadCount] = useState(0);
    const [page, setPage] = useState(0);
    const limit = 15;

    const token = localStorage.getItem('token');
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

    const fetchNotifications = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const params = {
                skip: page * limit,
                limit,
                ...(activeCategory !== 'all' && { category: activeCategory }),
            };
            const response = await axios.get(`${BACKEND_URL}/api/notifications`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });
            setNotifications(response.data.notifications || []);
            setTotal(response.data.total || 0);
            setUnreadCount(response.data.unread_count || 0);
        } catch (err) {
            console.error('Fetch notifications error:', err);
            toast.error('Failed to load notifications');
        } finally {
            setLoading(false);
        }
    }, [token, page, activeCategory, BACKEND_URL]);

    useEffect(() => {
        fetchNotifications();

        // Poll every 30 seconds for real-time notification updates
        const pollInterval = setInterval(() => {
            fetchNotifications();
        }, 30000);
        return () => clearInterval(pollInterval);
    }, [fetchNotifications]);

    const handleMarkAllRead = async () => {
        try {
            await axios.put(`${BACKEND_URL}/api/notifications/mark-all-read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
            toast.success('All notifications marked as read');
        } catch (err) {
            toast.error('Failed to mark all as read');
        }
    };

    const handleMarkRead = async (notifId) => {
        try {
            await axios.put(`${BACKEND_URL}/api/notifications/${notifId}/read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Mark read error:', err);
        }
    };

    const handleNotificationClick = (notif) => {
        if (!notif.read) handleMarkRead(notif.id);

        const meta = notif.metadata || {};
        if (meta.tracking_url) {
            window.open(meta.tracking_url, '_blank');
        } else if (meta.order_id && notif.category === 'orders') {
            navigate('/store?orders=1');
        } else if (meta.comic_id) {
            navigate(`/comic/${meta.comic_id}`);
        } else if (meta.from_username) {
            navigate(`/profile/${meta.from_username}`);
        }
    };

    const timeAgo = (dateStr) => {
        const now = new Date();
        const date = new Date(dateStr);
        const seconds = Math.floor((now - date) / 1000);
        if (seconds < 60) return 'Just now';
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        if (days < 7) return `${days}d ago`;
        return date.toLocaleDateString();
    };

    const totalPages = Math.ceil(total / limit);

    if (!user) {
        return (
            <div className="min-h-screen flex items-center justify-center text-white" style={{ background: 'linear-gradient(180deg, #0a0e17 0%, #0f1629 100%)' }}>
                <div className="text-center space-y-4">
                    <Bell className="w-16 h-16 mx-auto text-gray-600" />
                    <h2 className="text-2xl font-bold">Sign in to view notifications</h2>
                    <Button onClick={() => navigate('/login')} className="bg-purple-600 hover:bg-purple-700">Log In</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen text-white pb-20" style={{ background: 'linear-gradient(180deg, #0a0e17 0%, #0f1629 50%, #0a0e17 100%)' }}>
            <div className="max-w-3xl mx-auto px-4 pt-24">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" onClick={() => navigate(-1)} className="text-gray-400 hover:text-white p-2">
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold flex items-center gap-2">
                                <Bell className="w-6 h-6 text-purple-400" />
                                Notifications
                                {unreadCount > 0 && (
                                    <span className="ml-2 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-500 text-white animate-pulse">
                                        {unreadCount}
                                    </span>
                                )}
                            </h1>
                            <p className="text-gray-500 text-sm">{total} notifications</p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        {unreadCount > 0 && (
                            <Button onClick={handleMarkAllRead} variant="outline" className="border-gray-700 text-gray-300 hover:text-white text-sm">
                                <CheckCheck className="w-4 h-4 mr-1" /> Mark all read
                            </Button>
                        )}
                        <Button onClick={fetchNotifications} variant="ghost" className="text-gray-400 hover:text-white p-2">
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </Button>
                    </div>
                </div>

                {/* Category Filter Tabs */}
                <div className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hidden">
                    {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                        const Icon = config.icon;
                        const isActive = activeCategory === key;
                        return (
                            <button
                                key={key}
                                onClick={() => { setActiveCategory(key); setPage(0); }}
                                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap border ${isActive
                                    ? `bg-gradient-to-r ${config.color} text-white border-transparent shadow-lg`
                                    : 'bg-gray-800/50 text-gray-400 border-gray-700/50 hover:border-gray-600 hover:text-white'
                                    }`}
                            >
                                <Icon className="w-3.5 h-3.5" />
                                {config.label}
                            </button>
                        );
                    })}
                </div>

                {/* Notification List */}
                <div className="space-y-2">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                            <RefreshCw className="w-8 h-8 animate-spin mb-3" />
                            <p>Loading notifications...</p>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                            <Bell className="w-16 h-16 mb-4 opacity-30" />
                            <h3 className="text-lg font-semibold text-gray-400">No notifications yet</h3>
                            <p className="text-sm mt-1">We'll notify you when something happens</p>
                        </div>
                    ) : (
                        notifications.map(notif => {
                            const typeConfig = TYPE_ICONS[notif.type] || TYPE_ICONS.system;
                            const Icon = typeConfig.icon;
                            const catConfig = CATEGORY_CONFIG[notif.category] || CATEGORY_CONFIG.system;

                            return (
                                <Card
                                    key={notif.id}
                                    onClick={() => handleNotificationClick(notif)}
                                    className={`border transition-all cursor-pointer group ${notif.read
                                        ? 'bg-gray-900/40 border-gray-800/50 hover:border-gray-700'
                                        : 'bg-gray-900/80 border-purple-500/30 hover:border-purple-500/60 shadow-md shadow-purple-500/5'
                                        }`}
                                >
                                    <CardContent className="p-4">
                                        <div className="flex items-start gap-3">
                                            {/* Icon */}
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${typeConfig.bg}`}>
                                                <Icon className={`w-5 h-5 ${typeConfig.color}`} />
                                            </div>

                                            {/* Content */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-0.5">
                                                    <span className={`text-sm font-semibold ${notif.read ? 'text-gray-300' : 'text-white'}`}>
                                                        {notif.title}
                                                    </span>
                                                    {!notif.read && (
                                                        <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse shrink-0" />
                                                    )}
                                                </div>
                                                <p className={`text-sm leading-relaxed ${notif.read ? 'text-gray-500' : 'text-gray-300'}`}>
                                                    {notif.message}
                                                </p>

                                                {/* Tracking Link for Order Notifications */}
                                                {notif.metadata?.tracking_url && (
                                                    <a
                                                        href={notif.metadata.tracking_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-lg bg-cyan-500/10 text-cyan-400 text-xs font-semibold hover:bg-cyan-500/20 transition-all border border-cyan-500/20"
                                                    >
                                                        <Truck className="w-3.5 h-3.5" />
                                                        {notif.metadata.carrier && <span>{notif.metadata.carrier}</span>}
                                                        {notif.metadata.tracking_number && <span className="font-mono">{notif.metadata.tracking_number}</span>}
                                                        <ExternalLink className="w-3 h-3 ml-1" />
                                                    </a>
                                                )}

                                                <div className="flex items-center gap-3 mt-1.5">
                                                    <span className="text-xs text-gray-600">{timeAgo(notif.created_at)}</span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${catConfig.bg} ${catConfig.text}`}>
                                                        {catConfig.label}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Read indicator */}
                                            {!notif.read && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleMarkRead(notif.id); }}
                                                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-full hover:bg-white/10 text-gray-500 hover:text-white shrink-0"
                                                    title="Mark as read"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-4 mt-8 pb-8">
                        <Button
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                            disabled={page === 0}
                            variant="outline"
                            className="border-gray-700 text-gray-300"
                        >
                            <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                        </Button>
                        <span className="text-sm text-gray-500">
                            Page {page + 1} of {totalPages}
                        </span>
                        <Button
                            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                            disabled={page >= totalPages - 1}
                            variant="outline"
                            className="border-gray-700 text-gray-300"
                        >
                            Next <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Notifications;
